import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import axios from "axios";
import DOMPurify from "dompurify";
import { ArrowLeft, Calendar, User, Eye } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/blog/posts/${slug}`)
      .then(r => {
        setPost(r.data);
        // Fire-and-forget: increment view counter
        axios.post(`${API}/blog/posts/${slug}/view`).catch(() => {});
      })
      .catch(() => setError("Post not found."))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <div className="bg-[#F5F0E8] min-h-screen">
      <Navbar />
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-[#0A1628] border-t-transparent rounded-full animate-spin" />
      </div>
    </div>
  );

  if (error || !post) return (
    <div className="bg-[#F5F0E8] min-h-screen">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h1 className="font-serif text-3xl font-bold text-[#0A1628] mb-4">Post Not Found</h1>
        <Link to="/blog" className="text-[#C5A059] hover:underline">← Back to Blog</Link>
      </div>
      <Footer />
    </div>
  );

  return (
    <div className="bg-[#F5F0E8]">
      <SEO
        title={post.title}
        description={
          post.category === "Naval History"
            ? `${post.excerpt} — A post on WWI Naval History and Historical Accuracy by Donald Stephen Inbody, naval historian and author of The Morse Chronicles.`
            : post.excerpt
        }
        keywords={
          post.category === "Naval History"
            ? "WWI Naval History, Historical Accuracy, naval warfare, Donald Stephen Inbody"
            : post.category
        }
        canonical={`https://inbody.net/blog/${post.slug}`}
        ogType="article"
        publishedAt={post.created_at}
        category={post.category}
      />
      <Navbar />

      {/* Post Header */}
      <section className="pt-32 pb-12 border-b border-[#0A1628]/10">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <Link
            to="/blog"
            data-testid="back-to-blog"
            className="inline-flex items-center gap-2 text-[#2D3142]/60 hover:text-[#C5A059] text-sm mb-8 transition-colors"
          >
            <ArrowLeft size={14} /> Back to Blog
          </Link>
          <span className="inline-block bg-[#0A1628] text-[#C5A059] text-xs font-semibold px-3 py-1 uppercase tracking-wider mb-4">
            {post.category}
          </span>
          <h1
            data-testid="post-title"
            className="font-serif text-4xl sm:text-5xl font-bold text-[#0A1628] leading-tight mb-6"
          >
            {post.title}
          </h1>
          <div className="flex items-center gap-6 text-[#2D3142]/50 text-sm">
            <span className="flex items-center gap-2"><User size={14} />{post.author || "Donald Inbody"}</span>
            <span className="flex items-center gap-2"><Calendar size={14} />{formatDate(post.created_at)}</span>
            {post.views !== undefined && (
              <span className="flex items-center gap-2"><Eye size={14} />{((post.views ?? 0) + 1).toLocaleString()} reads</span>
            )}
          </div>
        </div>
      </section>

      {/* Post Content */}
      <article className="max-w-3xl mx-auto px-6 lg:px-8 py-16">
        <div
          data-testid="post-content"
          className="prose prose-lg max-w-none text-[#2D3142] leading-relaxed blog-content"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }}
        />

        {/* Author Bio */}
        <div className="mt-16 pt-8 border-t border-[#0A1628]/12">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-[#0A1628] flex items-center justify-center flex-shrink-0">
              <User size={20} className="text-[#C5A059]" />
            </div>
            <div>
              <p className="overline mb-1">Written by</p>
              <h4 className="font-serif text-lg font-bold text-[#0A1628] mb-1">Donald Inbody</h4>
              <p className="text-[#2D3142] text-sm leading-relaxed">
                Author, political scientist, naval historian, and amateur radio operator (ADØK).
              </p>
            </div>
          </div>
        </div>

        <div className="mt-10 flex justify-between items-center">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-[#0A1628] font-semibold text-sm hover:text-[#C5A059] transition-colors"
          >
            <ArrowLeft size={14} /> All Posts
          </Link>
          <Link
            to={`/blog?category=${encodeURIComponent(post.category)}`}
            className="inline-flex items-center gap-2 text-sm text-[#2D3142] hover:text-[#0A1628] transition-colors border border-[#0A1628]/20 px-4 py-2"
          >
            More in {post.category}
          </Link>
        </div>

        {/* Read count */}
        <div className="mt-8 pt-4 border-t border-[#0A1628]/8 flex items-center gap-2 text-[#2D3142]/40 text-xs">
          <Eye size={12} />
          <span data-testid="post-view-count">{((post.views ?? 0) + 1).toLocaleString()} reads</span>
        </div>
      </article>

      <Footer />
    </div>
  );
}
